import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { foldQuestions, questionsAt } from "../../../api/content/questionFoldsApi";
import type { ItemChange, Review } from "../../../api/content/reviewDecoders";
import { newClientKey } from "../../../api/threads/threadsApi";
import ErrorNotice from "../../../errors/ErrorNotice";
import { useLoad } from "../../../shared/loading/useLoad";
import { useSubmission } from "../../../shared/state/useSubmission";
import AppButton from "../../../ui/buttons/AppButton";
import LoadStatus from "../../../ui/feedback/LoadStatus";
import Field from "../../../ui/forms/Field";
import FieldGroup from "../../../ui/forms/FieldGroup";
import OptionLabel from "../../../ui/forms/OptionLabel";
import { FORM_COMPACT_CONTROL_CLASS, FORM_SELECT_CLASS } from "../../../ui/styles/formStyles";
import { waitingReviewStep } from "../inbox/reviewAddress";

interface QuestionFoldProps {
  readonly item: ItemChange;
  readonly review: Review;
  readonly onChanged: () => void;
}

/**
 * Folding the competency question in focus with others at the head (D6 fold; Johannes, 3 Nov:
 * "Consolidate in DMWs review for now"): the question kept, the ones folded into it, and
 * optionally its new words. Offered only where the server takes it: while the review step
 * waits, to a curator of this question, for a question still at the head.
 */
export default function QuestionFold({ item, review, onChanged }: QuestionFoldProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const curated = review.summary.callerCurates.some((location) => location.itemId === item.itemId);
  if (item.kind !== "competency_question" || item.change === "removed" || waitingReviewStep(review) === null || !curated) return null;
  if (!open) {
    return (
      <AppButton
        size="xs"
        onClick={() => {
          setOpen(true);
        }}
      >
        {t("reviews.fold.open")}
      </AppButton>
    );
  }
  return (
    <FoldForm
      item={item}
      review={review}
      onFolded={() => {
        setOpen(false);
        onChanged();
      }}
      onCancel={() => {
        setOpen(false);
      }}
    />
  );
}

function FoldForm({ item, review, onFolded, onCancel }: { item: ItemChange; review: Review; onFolded: () => void; onCancel: () => void }) {
  const { t } = useTranslation();
  const { projectId, changeSetId } = review.changeSet;
  const load = useCallback((signal: AbortSignal) => questionsAt(projectId, review.head, signal), [projectId, review.head]);
  const questions = useLoad(load);
  const [kept, setKept] = useState(item.itemId);
  const [chosen, setChosen] = useState<ReadonlySet<string>>(new Set());
  const [words, setWords] = useState<string | null>(null);
  const folding = useSubmission();
  if (questions.state.status !== "loaded") return <LoadStatus state={questions.state} loadingLabel={t("common.loading")} onRetry={questions.reload} compact />;
  const listed = questions.state.value;
  const keptWords = listed.find((question) => question.itemId === kept)?.question ?? "";
  const shownWords = words ?? keptWords;
  const others = listed.filter((question) => question.itemId !== kept);
  const fold = others.filter((question) => chosen.has(question.itemId)).map((question) => question.itemId);
  const newWords = shownWords.trim();
  const toggle = (itemId: string) => {
    const next = new Set(chosen);
    if (!next.delete(itemId)) next.add(itemId);
    setChosen(next);
  };
  const submit = () => {
    folding.submit(async () => {
      await foldQuestions(changeSetId, { reviewedHead: review.head, keep: kept, fold, question: newWords === keptWords ? null : newWords, clientKey: newClientKey() });
      onFolded();
    });
  };
  return (
    <div className="app-card space-y-2 p-3">
      <Field label={t("reviews.fold.keep")}>
        {(control) => (
          <select
            {...control}
            className={FORM_SELECT_CLASS}
            value={kept}
            onChange={(event) => {
              const next = event.target.value;
              setKept(next);
              setChosen(new Set(next === item.itemId ? [] : [item.itemId]));
              setWords(null);
            }}
          >
            {listed.map((question) => (
              <option key={question.itemId} value={question.itemId}>
                {question.question}
              </option>
            ))}
          </select>
        )}
      </Field>
      <FieldGroup legend={t("reviews.fold.fold")} className="space-y-1">
        {others.map((question) => (
          <OptionLabel key={question.itemId} align="start">
            <input
              type="checkbox"
              value={question.itemId}
              checked={chosen.has(question.itemId)}
              onChange={() => {
                toggle(question.itemId);
              }}
            />
            <span>{question.question}</span>
          </OptionLabel>
        ))}
      </FieldGroup>
      <Field label={t("reviews.fold.question")}>
        {(control) => (
          <input
            {...control}
            type="text"
            className={FORM_COMPACT_CONTROL_CLASS}
            value={shownWords}
            onChange={(event) => {
              setWords(event.target.value);
            }}
          />
        )}
      </Field>
      {folding.state.status === "failed" ? <ErrorNotice error={folding.state.error} compact /> : null}
      <div className="flex flex-wrap gap-2">
        <AppButton size="xs" variant="primary" disabled={fold.length === 0 || newWords === "" || folding.state.status === "submitting"} onClick={submit}>
          {t("reviews.fold.submit")}
        </AppButton>
        <AppButton size="xs" onClick={onCancel}>
          {t("common.cancel")}
        </AppButton>
      </div>
    </div>
  );
}
